import { Link } from "react-router-dom";
import {
  ArrowRight,
  MapPin,
  ShieldCheck,
  Building2,
  Sprout,
  TreePine,
  Waves,
  Route,
  Droplet,
  Trees,
  Flower2,
  Baby,
  Trophy,
  Home,
} from "lucide-react";

const HIGHLIGHTS = [
  { icon: Home, label: "Exclusive Township Living" },
  { icon: Route, label: "60 | 40 | 33 Feet Roads" },
  { icon: Waves, label: "Efficient Sewage System" },
  { icon: Droplet, label: "Rainwater Harvesting Pits" },
  { icon: ShieldCheck, label: "24/7 Security" },
  { icon: Flower2, label: "Landscaped Gardens" },
  { icon: TreePine, label: "Central Park" },
  { icon: Baby, label: "Kids Play Area" },
  { icon: Trees, label: "Avenue Plantation" },
  { icon: Building2, label: "3 Acre Clubhouse" },
  { icon: Waves, label: "Swimming Pool" },
  { icon: Trophy, label: "Outdoor Sports Facilities" },
];

export default function Landing() {
  return (
    <div className="min-h-screen bg-[#F9F8F6] text-[#1A1F16]">
      {/* Nav */}
      <header className="border-b border-[#E5E2DC] bg-white/80 backdrop-blur sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-4 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3 min-w-0">
            <img src="/images/anuttara_logo.jpg" alt="Anuttara Township" className="h-11 w-11 object-cover rounded-sm flex-shrink-0" />
            <div className="leading-tight min-w-0">
              <div className="font-heading font-semibold truncate">Anuttara Township</div>
              <div className="text-[10px] tracking-[0.2em] uppercase text-[#5C6653]">Phase 2 · Kamareddy</div>
            </div>
          </div>
          <div className="flex items-center gap-3 md:gap-5">
            <img src="/images/citysquare_logo.jpg" alt="City Square — Legacy Behind Every Landmark" className="hidden sm:block h-11 object-contain" data-testid="citysquare-logo" />
            <Link to="/login" data-testid="nav-login" className="text-sm px-4 py-2 hover:bg-[#F0EFEA] transition-colors">Login</Link>
            <Link to="/register" data-testid="nav-register" className="text-sm px-4 py-2 bg-[#234B36] text-white hover:bg-[#1A3A29] transition-colors">Register as Channel Partner <ArrowRight className="inline h-3 w-3 ml-1" /></Link>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="/images/aerial_render.jpg"
            alt="Anuttara Township Phase 2 aerial rendering"
            className="w-full h-full object-cover"
            data-testid="hero-image"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0F1D14]/85 via-[#0F1D14]/55 to-transparent" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0F1D14]/70 via-transparent to-transparent" />
        </div>
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-24 md:py-40 relative z-10">
          <div className="max-w-3xl">
            <img
              src="/images/anuttara_logo.jpg"
              alt="Anuttara Township Phase II"
              data-testid="hero-anuttara-logo"
              className="h-32 md:h-44 w-auto mb-8 rounded-sm shadow-2xl"
            />
            <div className="text-[11px] tracking-[0.3em] uppercase text-white/80 font-semibold mb-6">
              Master Layout · 7 LP Approvals · 1,400+ Plots
            </div>
            <h1 className="font-heading text-5xl sm:text-7xl leading-[0.95] text-white font-medium">
              Anuttara Township<br />
              <span className="text-[#F4C29A]">Phase 2 · Kamareddy</span>
            </h1>
            <p className="mt-8 text-white/90 text-lg max-w-xl">
              Verified plot inventory, interactive layouts and end-to-end booking control for the Anuttara sales & marketing team. Every plot mapped, every booking traceable, every approval accountable.
            </p>
            <div className="mt-10 flex flex-wrap gap-3">
              <Link to="/register" data-testid="hero-register" className="inline-flex items-center gap-2 bg-[#C06C47] hover:bg-[#A65A38] text-white px-6 py-3 transition-colors font-medium">
                Register as Channel Partner <ArrowRight className="h-4 w-4" />
              </Link>
              <Link to="/login" data-testid="hero-login" className="inline-flex items-center gap-2 border border-white/60 text-white hover:bg-white/10 px-6 py-3 transition-colors font-medium">
                Team Login
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Portal capabilities */}
      <section className="max-w-7xl mx-auto px-6 lg:px-12 py-20">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {[
            { icon: MapPin, title: "Interactive Layout Map", desc: "Zoom, pan and click any plot. Color-coded by real-time availability across 7 LP approvals." },
            { icon: Building2, title: "Live Plot Inventory", desc: "Search by plot number, filter by status, size and phase. Data sourced from official TLP registers." },
            { icon: ShieldCheck, title: "Admin-Controlled Bookings", desc: "Channel Partners submit; Admins & Super-Admins approve. Every action is auditable." },
          ].map((f, i) => (
            <div key={i} className="border border-[#E5E2DC] bg-white p-8">
              <f.icon className="h-6 w-6 text-[#234B36] mb-6" />
              <h3 className="font-heading text-xl mb-2">{f.title}</h3>
              <p className="text-sm text-[#5C6653] leading-relaxed">{f.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Project Highlights */}
      <section className="bg-[#234B36] text-white relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-20 md:py-24 relative z-10">
          <div className="flex items-baseline justify-between mb-12">
            <div>
              <div className="text-[10px] tracking-[0.3em] uppercase text-[#F4C29A] font-semibold mb-3">Anuttara Township · Phase II</div>
              <h2 className="font-heading text-4xl md:text-5xl">Project Highlights</h2>
            </div>
            <div className="hidden md:block text-right text-white/70 text-xs tracking-[0.2em] uppercase">
              Kamareddy · Telangana
            </div>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3" data-testid="highlights-grid">
            {HIGHLIGHTS.map((h, i) => (
              <div key={i} className="border border-white/15 hover:border-[#F4C29A]/60 bg-white/5 hover:bg-white/10 transition-colors px-5 py-6 flex items-start gap-4">
                <h.icon className="h-5 w-5 text-[#F4C29A] flex-shrink-0 mt-0.5" />
                <div className="text-sm font-medium leading-snug">{h.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="bg-[#F0EFEA]">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-16 grid grid-cols-2 md:grid-cols-4 gap-6">
          {[
            ["7", "LP Approvals"],
            ["1,400+", "Registered Plots"],
            ["8", "Phases"],
            ["148", "Acres (approx.)"],
          ].map(([n, l]) => (
            <div key={l} className="border-l-2 border-[#234B36] pl-5">
              <div className="font-heading text-4xl md:text-5xl text-[#234B36]">{n}</div>
              <div className="text-[11px] tracking-[0.3em] uppercase text-[#5C6653] mt-2">{l}</div>
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-7xl mx-auto px-6 lg:px-12 py-24">
        <div className="border border-[#E5E2DC] p-12 md:p-16 flex flex-col md:flex-row items-start md:items-center justify-between gap-8 bg-white">
          <div>
            <h2 className="font-heading text-3xl md:text-4xl mb-3">Join the sales network.</h2>
            <p className="text-[#5C6653] max-w-lg">Register as a sales agent — access will be granted after admin approval.</p>
          </div>
          <Link to="/register" data-testid="cta-register" className="inline-flex items-center gap-2 bg-[#234B36] hover:bg-[#1A3A29] text-white px-8 py-4 transition-colors font-medium">
            Register Now <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </section>

      <footer className="border-t border-[#E5E2DC] py-8 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div className="flex items-center gap-4">
            <img src="/images/anuttara_logo.jpg" alt="Anuttara" className="h-10 w-10 object-cover rounded-sm" />
            <div className="text-xs text-[#5C6653]">© Anuttara Township Phase 2 @ Kamareddy</div>
          </div>
          <div className="flex items-center gap-4">
            <img src="/images/citysquare_logo.jpg" alt="City Square" className="h-10 object-contain" />
            <div className="text-xs text-[#5C6653] italic hidden md:block">Legacy Behind Every Landmark</div>
          </div>
        </div>
      </footer>
    </div>
  );
}
